/* assets/pdp.js - AceZone PDP behavior
 * Owns:
 *   • Gallery thumbnail switching
 *   • Quantity stepper
 *   • Sticky-bar visibility (IntersectionObserver on the main CTA)
 *   • Sticky CTA proxy → submits the main product form
 *   • AJAX add-to-cart with loading state, fallback to native form submit
 *   • dataLayer / Shopify analytics events (preserves existing tracking shape)
 *
 * Does NOT replace the theme's existing cart drawer / cart count. After a
 * successful add, it dispatches `cart:refresh` so the theme's cart code
 * can update itself (most AceZone-style themes listen on this event).
 */
(function () {
  'use strict';

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }

  // ─── Gallery ────────────────────────────────────────────────────────────
  function initGallery(scope) {
    var gallery = $('[data-pdp-gallery]', scope);
    if (!gallery) return;
    var thumbs = $$('.gallery__thumb', gallery);
    var imgs   = $$('[data-stage-img]', gallery);
    var caption= $('[data-gallery-caption]', gallery);
    var stage  = $('[data-gallery-stage]', gallery);
    if (!imgs.length) return;

    function currentIndex() {
      for (var i = 0; i < imgs.length; i++) {
        if (imgs[i].dataset.active === 'true') return i;
      }
      return 0;
    }

    // Show the image (and sync the thumbnail rail) at idx, wrapping around.
    function select(idx) {
      idx = (idx + imgs.length) % imgs.length;
      thumbs.forEach(function (t) {
        t.setAttribute('aria-current', parseInt(t.dataset.thumbIndex, 10) === idx ? 'true' : 'false');
      });
      imgs.forEach(function (img) {
        var match = parseInt(img.dataset.stageImg, 10) === idx;
        if (match) {
          img.hidden = false;
          img.dataset.active = 'true';
          if (caption && img.alt) caption.textContent = img.alt.slice(0, 24);
        } else {
          img.hidden = true;
          delete img.dataset.active;
        }
      });
    }

    thumbs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        select(parseInt(btn.dataset.thumbIndex, 10));
      });
    });

    // Mobile: swipe left/right across the main image to page through photos,
    // mirroring the thumbnail rail (previously the only way to change image on
    // touch). Touch-only + horizontal-dominant so it never fights vertical page
    // scroll, and wraps like the lightbox. A genuine tap (little movement) still
    // falls through to initZoom's tap-to-open-lightbox; a swipe moves past the
    // 40px tap-slop so no click fires. The inline stage keeps its absolute-inset
    // layout (the documented iOS Safari aspect-ratio crop fix) — we swap the
    // active image rather than converting the stage into a scroll carousel.
    if (stage && imgs.length > 1) {
      var startX = 0, startY = 0, tracking = false;
      stage.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 1) { tracking = false; return; }
        tracking = true;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });
      stage.addEventListener('touchend', function (e) {
        if (!tracking) return;
        tracking = false;
        var dx = e.changedTouches[0].clientX - startX;
        var dy = e.changedTouches[0].clientY - startY;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          select(currentIndex() + (dx < 0 ? 1 : -1));
        }
      }, { passive: true });
    }
  }

  // ─── Image zoom / lightbox ────────────────────────────────────────────────
  // The gallery's zoom chip *and* the main image itself open a full-screen
  // lightbox built from the stage images. Click/tap the image to magnify
  // (origin follows the cursor); arrows, prev/next buttons, or a horizontal
  // swipe on touch devices cycle; Esc or backdrop click closes.
  function initZoom(scope) {
    var gallery = $('[data-pdp-gallery]', scope);
    if (!gallery) return;
    var zoomBtn = $('[data-pdp-zoom]', gallery);
    if (!zoomBtn) return;

    // Stage photos only (the zoom chip holds an <svg>, not an <img>).
    var stageImgs = $$('.gallery__main img', gallery);
    if (!stageImgs.length) return;

    var t = {
      close: zoomBtn.dataset.zoomClose || 'Close',
      prev:  zoomBtn.dataset.zoomPrev  || 'Previous image',
      next:  zoomBtn.dataset.zoomNext  || 'Next image',
      hint:  zoomBtn.dataset.zoomHint  || ''
    };

    // Bump the Shopify CDN width param so the lightbox shows a crisper image.
    function hiRes(url) { return url ? url.replace(/([?&]width=)\d+/, '$12048') : url; }

    var sources = stageImgs.map(function (img) {
      return { src: hiRes(img.getAttribute('src')), alt: img.getAttribute('alt') || '' };
    });
    var multi = sources.length > 1;

    var box = null, imgEl, counterEl, closeBtn, prevBtn, nextBtn;
    var current = 0, zoomed = false, lastFocus = null;

    // Preload every hi-res source the first time the lightbox opens, so paging
    // between images is instant (cache hit) instead of fetching each on demand.
    // Done on open — not on page load — to avoid pulling 2048px images for
    // visitors who never zoom. Refs are kept so nothing is GC'd mid-fetch.
    var preloaded = false, preloadCache = [];
    function preloadAll() {
      if (preloaded) return;
      preloaded = true;
      sources.forEach(function (s) {
        if (!s.src) return;
        var img = new Image();
        img.decoding = 'async';
        img.src = s.src;
        preloadCache.push(img);
      });
    }

    function activeIndex() {
      for (var i = 0; i < stageImgs.length; i++) {
        if (stageImgs[i].dataset.active === 'true') return i;
      }
      return 0;
    }

    function setZoom(on) {
      zoomed = on;
      imgEl.classList.toggle('lightbox__img--zoomed', on);
      imgEl.style.transform = on ? 'scale(2)' : '';
      if (!on) imgEl.style.transformOrigin = 'center center';
    }

    function show(i) {
      current = (i + sources.length) % sources.length;
      setZoom(false);
      imgEl.src = sources[current].src;
      imgEl.alt = sources[current].alt;
      if (counterEl) counterEl.textContent = (current + 1) + ' / ' + sources.length;
    }

    function onKey(e) {
      if (e.key === 'Escape') { close(); return; }
      if (multi && e.key === 'ArrowLeft')  { show(current - 1); return; }
      if (multi && e.key === 'ArrowRight') { show(current + 1); return; }
      if (e.key === 'Tab') {
        var f = [closeBtn, prevBtn, nextBtn].filter(Boolean);
        if (f.length < 2) { e.preventDefault(); f[0] && f[0].focus(); return; }
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }

    function svgBtn(cls, label, path) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = cls;
      b.setAttribute('aria-label', label);
      b.innerHTML = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + path + '</svg>';
      return b;
    }

    function open() {
      if (box) return;
      lastFocus = document.activeElement;
      current = activeIndex();

      box = document.createElement('div');
      box.className = 'lightbox';
      box.setAttribute('role', 'dialog');
      box.setAttribute('aria-modal', 'true');
      box.setAttribute('aria-label', zoomBtn.getAttribute('aria-label') || 'Image viewer');

      closeBtn = svgBtn('lightbox__close', t.close, '<path d="M18 6 6 18M6 6l12 12"/>');

      var stage = document.createElement('div');
      stage.className = 'lightbox__stage';
      // With a single image there are no nav columns - span the whole grid so it stays centered.
      if (!multi) stage.style.gridColumn = '1 / -1';
      imgEl = document.createElement('img');
      imgEl.className = 'lightbox__img';
      imgEl.setAttribute('draggable', 'false');
      stage.appendChild(imgEl);

      box.appendChild(closeBtn);
      if (multi) { prevBtn = svgBtn('lightbox__nav lightbox__nav--prev', t.prev, '<path d="m15 18-6-6 6-6"/>'); box.appendChild(prevBtn); }
      box.appendChild(stage);
      if (multi) { nextBtn = svgBtn('lightbox__nav lightbox__nav--next', t.next, '<path d="m9 18 6-6-6-6"/>'); box.appendChild(nextBtn); }
      if (multi) { counterEl = document.createElement('div'); counterEl.className = 'lightbox__counter'; box.appendChild(counterEl); }
      if (t.hint) { var h = document.createElement('div'); h.className = 'lightbox__hint'; h.textContent = t.hint; box.appendChild(h); }

      closeBtn.addEventListener('click', close);
      if (prevBtn) prevBtn.addEventListener('click', function () { show(current - 1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { show(current + 1); });
      box.addEventListener('click', function (e) { if (e.target === box) close(); });
      imgEl.addEventListener('click', function (e) { e.stopPropagation(); setZoom(!zoomed); });
      // Magnify toward the cursor. Measure against the stage (fixed) rather than
      // the image (which moves as the origin shifts) to avoid feedback jitter.
      stage.addEventListener('pointermove', function (e) {
        if (!zoomed) return;
        var r = stage.getBoundingClientRect();
        var px = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
        var py = Math.max(0, Math.min(1, (e.clientY - r.top) / r.height));
        imgEl.style.transformOrigin = (px * 100) + '% ' + (py * 100) + '%';
      });

      // Horizontal swipe to page images on touch devices. Ignored while zoomed
      // (so the pinch/pan gesture isn't hijacked) and for single-image galleries.
      if (multi) {
        var swipeX = 0, swipeY = 0, swiping = false;
        stage.addEventListener('touchstart', function (e) {
          if (zoomed || e.touches.length !== 1) { swiping = false; return; }
          swiping = true;
          swipeX = e.touches[0].clientX;
          swipeY = e.touches[0].clientY;
        }, { passive: true });
        stage.addEventListener('touchend', function (e) {
          if (!swiping) return;
          swiping = false;
          var dx = e.changedTouches[0].clientX - swipeX;
          var dy = e.changedTouches[0].clientY - swipeY;
          // Require a mostly-horizontal move past a small threshold.
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
            show(dx < 0 ? current + 1 : current - 1);
          }
        }, { passive: true });
      }

      document.body.appendChild(box);
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('keydown', onKey);
      show(current);
      preloadAll();
      closeBtn.focus();
    }

    function close() {
      if (!box) return;
      document.removeEventListener('keydown', onKey);
      document.documentElement.style.overflow = '';
      box.remove();
      box = null;
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    zoomBtn.addEventListener('click', open);

    // Clicking the main product image (not just the magnifier chip) opens the
    // lightbox too. open() no-ops if it's already open, so this is safe.
    stageImgs.forEach(function (img) {
      img.style.cursor = 'zoom-in';
      img.addEventListener('click', open);
    });
  }

  // ─── Qty stepper ────────────────────────────────────────────────────────
  function initQty(scope) {
    var input = $('[data-qty-input]', scope);
    if (!input) return;
    var down = $('[data-qty-down]', scope);
    var up   = $('[data-qty-up]', scope);
    var min  = parseInt(input.min || '1', 10);
    var max  = parseInt(input.max || '99', 10);
    function set(v) {
      v = Math.max(min, Math.min(max, v));
      input.value = v;
      if (down) down.disabled = v <= min;
      if (up)   up.disabled   = v >= max;
    }
    if (down) down.addEventListener('click', function () { set(parseInt(input.value, 10) - 1); });
    if (up)   up.addEventListener('click',   function () { set(parseInt(input.value, 10) + 1); });
    input.addEventListener('change', function () { set(parseInt(input.value, 10) || min); });
    set(parseInt(input.value, 10) || min);
  }

  // ─── Sticky bar ─────────────────────────────────────────────────────────
  function initStickyBar() {
    var sticky = $('[data-pdp-sticky]');
    var mainCta = $('#pdp-main-cta');
    if (!sticky || !mainCta) return;

    var cartDrawerOpen = false;
    var mainCtaHidden = false;

    function updateStickyVisibility() {
      sticky.classList.toggle('sticky-bar--visible', mainCtaHidden && !cartDrawerOpen);
    }

    var io = new IntersectionObserver(function (entries) {
      mainCtaHidden = !entries[0].isIntersecting;
      updateStickyVisibility();
    }, { rootMargin: '0px 0px -60px 0px', threshold: 0 });
    io.observe(mainCta);

    // Hide sticky when cart drawer is open, restore when it closes
    var cartDrawer = document.getElementById('sidebar-cart');
    if (cartDrawer) {
      new MutationObserver(function () {
        cartDrawerOpen = cartDrawer.getAttribute('aria-hidden') === 'false';
        updateStickyVisibility();
      }).observe(cartDrawer, { attributes: true, attributeFilter: ['aria-hidden'] });
    }

    // Sticky CTA proxies the main form submit
    var stickyCta = $('[data-pdp-sticky-cta]', sticky);
    if (stickyCta) {
      stickyCta.addEventListener('click', function () {
        if (stickyCta.disabled) return;
        var formId = sticky.dataset.formTarget;
        var form = formId ? document.getElementById(formId) : null;
        if (form) {
          form.requestSubmit ? form.requestSubmit() : form.submit();
        } else {
          mainCta.click();
        }
      });
    }
  }

  // ─── AJAX add-to-cart ───────────────────────────────────────────────────
  function initAddToCart(scope) {
    var form = scope.tagName === 'FORM' ? scope : $('form[data-type="add-to-cart-form"]', scope);
    if (!form) return;
    var cta = $('#pdp-main-cta', form);
    if (!cta) return;
    var label = $('[data-cta-label]', cta);
    var originalLabel = label ? label.textContent : '';

    form.addEventListener('submit', function (e) {
      if (cta.disabled) { e.preventDefault(); return; }
      e.preventDefault();

      cta.disabled = true;
      cta.classList.add('cta--loading');
      cta.setAttribute('aria-busy', 'true');

      var fd = new FormData(form);

      fetch('/cart/add.js', {
        method: 'POST',
        body: fd,
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' }
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (j) { throw j; });
          return res.json();
        })
        .then(function (line) {
          // Restore CTA → success flash
          cta.classList.remove('cta--loading');
          cta.removeAttribute('aria-busy');
          var addedText = (label && label.dataset.addedLabel) || 'Added ✓';
          if (label) label.textContent = addedText;
          // Mirror the same flash on the sticky CTA
          var stickyCtaLabel = document.querySelector('[data-pdp-sticky-cta] span');
          if (stickyCtaLabel) {
            var origStickyText = stickyCtaLabel.textContent;
            stickyCtaLabel.textContent = addedText;
            setTimeout(function () { stickyCtaLabel.textContent = origStickyText; }, 1800);
          }
          setTimeout(function () {
            cta.disabled = false;
            if (label) label.textContent = originalLabel;
          }, 1800);

          // Analytics - preserves existing tracking event names
          var detail = {
            id: line.id || line.variant_id,
            variant_id: line.variant_id,
            product_id: line.product_id,
            quantity: line.quantity || 1,
            price: line.price,
            title: line.product_title || line.title
          };
          // Generic event other PDP/cart code can listen to
          document.dispatchEvent(new CustomEvent('pdp:add-to-cart', { detail: detail, bubbles: true }));
          // Theme listens for product:added on document to re-render the cart
          document.dispatchEvent(new CustomEvent('product:added', {
            bubbles: true,
            detail: { variant: { id: detail.variant_id }, quantity: detail.quantity }
          }));
          // Theme's documentDelegate listens for cart:refresh on document.documentElement
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { detail: detail, bubbles: true }));
          // GTM dataLayer push (only if dataLayer exists - non-breaking)
          if (window.dataLayer && typeof window.dataLayer.push === 'function') {
            window.dataLayer.push({
              event: 'add_to_cart',
              ecommerce: {
                currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'DKK',
                value: (line.price || 0) / 100 * (line.quantity || 1),
                items: [{
                  item_id: String(line.product_id || ''),
                  item_name: line.product_title || line.title,
                  item_variant: line.variant_title,
                  price: (line.price || 0) / 100,
                  quantity: line.quantity || 1
                }]
              }
            });
          }
        })
        .catch(function (err) {
          cta.classList.remove('cta--loading');
          cta.removeAttribute('aria-busy');
          cta.disabled = false;
          if (label) label.textContent = originalLabel;
          console.error('[pdp] add-to-cart failed', err);
          // Visible inline error (announce via aria-live)
          var actions = cta.closest('.actions');
          if (actions) {
            var msg = actions.nextElementSibling && actions.nextElementSibling.classList.contains('pdp-error')
              ? actions.nextElementSibling
              : null;
            if (!msg) {
              msg = document.createElement('div');
              msg.className = 'pdp-error';
              msg.setAttribute('role', 'alert');
              msg.style.cssText = 'color: var(--az-stock-out); font-size: 13px; padding-top: 4px;';
              actions.insertAdjacentElement('afterend', msg);
            }
            msg.textContent = (err && err.description) || 'Could not add to cart. Please try again.';
          }
        });
    });
  }

  // ─── Lazy YouTube video (+ optional thumbnail playlist) ────────────────
  function loadYouTube(frame, id, title) {
    if (!frame || !id) return;
    var iframe = document.createElement('iframe');
    iframe.src = 'https://www.youtube.com/embed/' + id + '?autoplay=1&rel=0';
    iframe.title = title || frame.dataset.videoTitle || 'Product video';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.frameBorder = '0';
    iframe.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;border:0;';
    frame.innerHTML = '';
    frame.appendChild(iframe);
  }

  function initLazyVideo(scope) {
    var root = scope || document;

    // Click-to-play: swap the poster for the iframe of the frame's current id.
    root.querySelectorAll('[data-pdp-video]').forEach(function (frame) {
      var btn = frame.querySelector('.vid__play');
      if (!btn) return;
      btn.addEventListener('click', function () {
        loadYouTube(frame, frame.dataset.ytId, frame.dataset.videoTitle);
      });
    });

    // Thumbnail playlist: clicking a thumb swaps + plays that video in its frame.
    root.querySelectorAll('[data-yt-gallery]').forEach(function (gallery) {
      var frame = gallery.querySelector('[data-pdp-video]');
      if (!frame) return;
      gallery.querySelectorAll('.vid__thumb').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var id = thumb.dataset.ytId;
          if (!id) return;
          frame.dataset.ytId = id;
          if (thumb.dataset.videoTitle) frame.dataset.videoTitle = thumb.dataset.videoTitle;
          gallery.querySelectorAll('.vid__thumb[aria-current="true"]').forEach(function (a) {
            a.setAttribute('aria-current', 'false');
          });
          thumb.setAttribute('aria-current', 'true');
          loadYouTube(frame, id, thumb.dataset.videoTitle);
        });
      });
    });
  }

  // ─── Scroll reveals + counter animations ────────────────────────────────
  function initMotion(scope) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var root = scope || document;

    // Stagger child reveals under each section root
    var groups = [
      '.buybox__eyebrow', '.buybox__title', '.buybox__tagline',
      '.rating', '.press', '.bullets', '.price', '.stock',
      '.actions', '.cta-secondary', '.trust', '.pay',
      '.section__eyebrow', '.section__title', '.section__intro',
      '.hero-feature__copy > *', '.hero-feature__figure',
      '.feat-split__copy > *', '.feat-split__media',
      '.app-show__copy > *', '.app-show__media',
      '.lineup__head > *', '.lineup__chart', '.lineup__card',
      '.otm__head > *', '.otm__card',
      '.box__copy > *', '.box__media',
      '.vid__head > *', '.vid__frame',
      '.faq__head > *', '.faq__item',
      '.hl-grid > .hl-card',
      '.usp', '.review', '.spec',
    ];
    groups.forEach(function (sel) {
      root.querySelectorAll(sel).forEach(function (el) {
        if (!el.classList.contains('reveal')) {
          el.classList.add('reveal');
          var peers = el.parentElement ? Array.prototype.slice.call(el.parentElement.children).filter(function (c) { return c.classList.contains('reveal'); }) : [];
          var idx = peers.indexOf(el);
          el.style.setProperty('--reveal-delay', Math.min(idx, 4) * 90 + 'ms');
        }
      });
    });

    var revealIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('reveal--in');
          revealIO.unobserve(e.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.08 });
    root.querySelectorAll('.reveal').forEach(function (el) { revealIO.observe(el); });

    // Counter animations for stat numbers
    var counterSelectors = ['.feat-split__stat__big', '.usp__metric__big'];
    var parseTarget = function (text) {
      var m = text.match(/([\d.,]+)/);
      if (!m) return null;
      var raw = m[1].replace(',', '.');
      return { num: parseFloat(raw), originalText: text, decimals: (raw.split('.')[1] || '').length };
    };
    var animateCounter = function (el, target) {
      var start = performance.now();
      var duration = 1400;
      var easeOut = function (t) { return 1 - Math.pow(1 - t, 3); };
      var suffix = target.originalText.replace(/^[\d.,]+/, '');
      var hasComma = target.originalText.indexOf(',') >= 0;
      var step = function (now) {
        var t = Math.min(1, (now - start) / duration);
        var v = target.num * easeOut(t);
        var s = v.toFixed(target.decimals);
        if (hasComma) s = s.replace('.', ',');
        el.textContent = s + suffix;
        if (t < 1) requestAnimationFrame(step);
        else el.textContent = target.originalText;
      };
      requestAnimationFrame(step);
    };
    var counterIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          var target = parseTarget(e.target.dataset.original || e.target.textContent);
          if (!target) return;
          if (!e.target.dataset.original) e.target.dataset.original = e.target.textContent;
          e.target.classList.add('counter');
          e.target.textContent = '0' + target.originalText.replace(/^[\d.,]+/, '');
          animateCounter(e.target, target);
          counterIO.unobserve(e.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.5 });
    counterSelectors.forEach(function (sel) {
      root.querySelectorAll(sel).forEach(function (el) { counterIO.observe(el); });
    });
  }

  // ─── Smooth-scroll jump links ─────────────────────────────────────────────
  // Any in-PDP link whose href is an on-page anchor (#some-id) - highlight cards,
  // the "Compare the lineup" CTA, rich-text buttons, or any anchor a merchant
  // adds - scrolls smoothly to the matching element. Falls back to a plain hash
  // jump if JS is off or the target isn't found.
  function initJumpLinks(scope) {
    $$('a[href^="#"], [data-pdp-jump], [data-pdp-lineup-link]', scope).forEach(function (link) {
      link.addEventListener('click', function (e) {
        var hash = link.getAttribute('href') || '';
        if (hash.charAt(0) !== '#' || hash.length < 2) return;
        var target = document.getElementById(hash.slice(1));
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  // ─── Buy-box description: clamp + read more / less ────────────────────────
  // The full product description is in the DOM and rendered clamped by default
  // (markup carries the clamp class, so there's no flash of the full copy on
  // load). We add the fade + surface the toggle only when the text actually
  // overflows that clamp; short copy gets unclamped so it reads as a plain tagline.
  function initDescription(scope) {
    var wrap = $('[data-buybox-desc]', scope);
    if (!wrap) return;
    var text   = $('[data-buybox-desc-text]', wrap);
    var toggle = $('[data-buybox-desc-toggle]', wrap);
    var label  = $('[data-buybox-desc-label]', wrap);
    if (!text || !toggle) return;

    var CLAMP = 'buybox__desc-text--clamped';   // max-height clip (also set in markup)
    var FADE  = 'buybox__desc-text--faded';      // bottom fade hint
    var moreLabel = toggle.getAttribute('data-label-more') || 'Read more';
    var lessLabel = toggle.getAttribute('data-label-less') || 'Read less';

    function isExpanded() { return toggle.getAttribute('aria-expanded') === 'true'; }
    function overflows() { return text.scrollHeight - text.clientHeight > 2; }

    // Decide whether the toggle is needed - only meaningful while collapsed.
    function evaluate() {
      if (isExpanded()) return;
      text.classList.add(CLAMP);
      if (overflows()) {
        text.classList.add(FADE);
        toggle.hidden = false;
      } else {
        text.classList.remove(CLAMP);
        text.classList.remove(FADE);
        toggle.hidden = true;
      }
    }

    toggle.addEventListener('click', function () {
      if (isExpanded()) {
        text.classList.add(CLAMP);
        text.classList.add(FADE);
        toggle.setAttribute('aria-expanded', 'false');
        if (label) label.textContent = moreLabel;
      } else {
        text.classList.remove(CLAMP);
        text.classList.remove(FADE);
        toggle.setAttribute('aria-expanded', 'true');
        if (label) label.textContent = lessLabel;
      }
    });

    evaluate();

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(evaluate, 150);
    });
  }

  // ─── Init on DOM ready ──────────────────────────────────────────────────
  function init() {
    var section = $('[data-section-type="main-product-pdp"]');
    if (!section) return;
    initGallery(section);
    initZoom(section);
    initQty(section);
    initDescription(section);
    initAddToCart(section);
    initStickyBar();
    initLazyVideo(section);
    initJumpLinks(section);
    initMotion(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify section editor reloads
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-section-type="main-product-pdp"]')) {
      init();
    }
  });
})();
